import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import type { PreviousEvaluationResult } from "@services/evaluationService";
import { EVALUATION_VERDICT_LABELS, type EvaluationVerdict } from "@constants/evaluationConstants";
import styles from "./EvaluationCard.module.css";

import approveIcon from "@assets/icons/approve-tick.svg";
import overrideIcon from "@assets/icons/edit-icon.svg";
import MathjaxComponent from "@components/mathjaxcomponent/MathjaxComponent";
import { useShortcut } from "@hooks/useShortCut";

interface L1EvaluationCardProps {
  result: PreviousEvaluationResult;
  maxMarks: number;
  onAccept: () => void;
  onOverride: () => void;
  isAccepted: boolean;
}

const ApproveIcon = () => (
  <img src={approveIcon} alt="Approve" width={17} height={17} />
);

const OverrideIcon = () => (
  <img src={overrideIcon} alt="Override" width={16} height={16} />
);

const VerdictBadge = ({ verdict }: { verdict: EvaluationVerdict }) => {
  const getVerdictStyle = (verdict: EvaluationVerdict) => {
    switch (verdict) {
      case "CORRECT":
        return { backgroundColor: "#10B981", color: "#ffffff" };
      case "INCORRECT":
        return { backgroundColor: "#EF4444", color: "#ffffff" };
      case "PARTIALLY_CORRECT":
        return { backgroundColor: "#F59E0B", color: "#ffffff" };
      case "UNATTEMPTED":
        return { backgroundColor: "#6B7280", color: "#ffffff" };
      default:
        return { backgroundColor: "#aaa3f7", color: "#ffffff" };
    }
  };

  return (
    <span 
      className={styles.evaluationVerdictBadge}
      style={getVerdictStyle(verdict)}
    >
      {EVALUATION_VERDICT_LABELS[verdict]}
    </span>
  );
};

export default function L1EvaluationCard({
  result,
  maxMarks,
  onAccept,
  onOverride,
  isAccepted,
}: L1EvaluationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const feedbackText = result.feedbacks.join("\n");
  const shouldShowReadMore = feedbackText.length > 60;
  const awardedMarksForDisplay=parseFloat(result?.awarded_marks.toString());
  useShortcut("alt+a", onAccept);
  useShortcut("alt+o", onOverride);
  useShortcut("alt+\\", () => {
    setIsExpanded(prev => !prev);
  });

  return (
    <div className={styles.l1Card}>
      {/* {!isAccepted && (
        <span className={styles.scrutinyText}>
          This evaluation requires scrutiny.
        </span>
      )} */}
      <div className={styles.cardHeader}>
        <div className={styles.labelRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isAccepted && <div className={styles.yellowDot} />}
            <span className={styles.cardTitle}>L1 Evaluation</span>
          </div>
        </div>
        <div className={styles.badgeHolder}>
        <AnimatePresence>
          {isAccepted && (
            <motion.div
              className={styles.acceptedBadge}
              initial={{
                scale: 1.5,
                opacity: 0,
                rotate: -5,
              }}
              animate={{
                scale: 1,
                opacity: 1,
                rotate: 0,
              }}
              exit={{
                scale: 0.8,
                opacity: 0,
              }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
                mass: 0.5,
              }}
            >
              <ApproveIcon />
              <span className={styles.acceptedBadgeText}>SME Accepted</span>
            </motion.div>
          )}
        </AnimatePresence>
        <VerdictBadge verdict={result.evaluation_verdict} />
        </div>
      </div>

      <span className={styles.score}>
        {awardedMarksForDisplay} / {maxMarks}
      </span>

      <div className={styles.feedbackContainer}>
        <span
          className={
            isExpanded || !shouldShowReadMore
              ? styles.feedbackText
              : styles.feedbackTextTruncated
          }
        >
          <MathjaxComponent data={feedbackText} />
        </span>
        {shouldShowReadMore && (
          <button title="Alt + \"
            className={styles.readMore}
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
          >
            {isExpanded ? "read less" : "read more"}
          </button>
        )}
      </div>

      <div className={styles.buttonRow}>
        <button title="Alt + A"
          className={styles.acceptButton}
          onClick={onAccept}
          disabled={isAccepted}
          type="button"
        >
          <span className={styles.acceptButtonText}>Accept</span>
        </button>
        <button title="Alt + O"
          className={styles.overrideButton}
          onClick={onOverride}
          type="button"
        >
          <OverrideIcon />
          <span className={styles.overrideButtonText}>Override</span>
        </button>
      </div>
    </div>
  );
}
